/**
 * Batch 4 · #1 · Help & Training dialog (Feb 2026)
 *
 * Owner directive: the sidebar Help & Training control was a live no-op
 * (it called `/api/docs/ops-manager-manual`, whose blob response is not
 * available in the live preview). Replaced the button behavior with a
 * plain in-app help dialog using current shadcn components. The former
 * PDF endpoint is intentionally LEFT IN PLACE for a possible later
 * restore — this dialog does NOT delete it, does NOT reference it.
 *
 * Content is deliberately short, nontechnical, and links back to
 * existing areas of the app. No engineering jargon, IDs, architecture,
 * Porsche / security content, external URLs, or new backend calls.
 */
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BuildStamp } from "@/components/BuildStamp";
import {
  CalendarRange, Printer, LayoutList, UserCheck, Compass,
} from "lucide-react";

const _TOPICS = [
  {
    key: "day-import",
    icon: CalendarRange,
    title: "Day & import state",
    body: (
      <>
        The <b>Day</b> workspace shows exactly one operational day at a time.
        A day starts out empty; you make it operational by importing its
        bookings. The header banner is the source of truth: it tells you
        whether the day is imported, empty, or ready to print.
      </>
    ),
    linkTo: "/day",
    linkLabel: "Open Day",
    linkTestid: "help-training-link-day",
  },
  {
    key: "import-update",
    icon: CalendarRange,
    title: "Import day",
    body: (
      <>
        Use <b>Import day</b> on the Day view to upload that day's
        Checkfront manifest. Running it again on an already-imported
        day is safe — new bookings are added, existing ones are updated,
        and your manual coach / car / module assignments are preserved
        when the underlying booking is still valid.
      </>
    ),
    linkTo: "/day",
    linkLabel: "Go to Day",
    linkTestid: "help-training-link-import",
  },
  {
    key: "walkin",
    icon: UserCheck,
    title: "Check walk-in",
    body: (
      <>
        When a guest arrives without a booking on file, use <b>Check walk-in</b>{" "}
        from the Day view to see if a matching Checkfront booking exists.
        If it does, one click brings it into today's schedule.
      </>
    ),
    linkTo: "/day",
    linkLabel: "Open Day",
    linkTestid: "help-training-link-walkin",
  },
  {
    key: "manifests",
    icon: Printer,
    title: "Manifests & printing",
    body: (
      <>
        Use <b>Print day</b> on the Day page or in <b>Manifests</b>.
        Choose a guest list, coach schedule, track and fleet sheet, or
        corporate event. Check the preview before printing. Accessible
        colors and other layouts are available in the same chooser.
        Review missing staffing and conflicts before handing out sheets.
      </>
    ),
    linkTo: "/manifests",
    linkLabel: "Open Manifests",
    linkTestid: "help-training-link-manifests",
  },
  {
    key: "programs",
    icon: LayoutList,
    title: "Programs & booking mappings",
    body: (
      <>
        <b>Programs</b> is your library of reusable program structures.
        Add activities and their coach and car requirements, then preview
        the schedule before adding it to a day. Publish a program to make
        it available for <b>Booking mappings</b>. When an import needs a
        new program, you can create and publish it without leaving the import.
        The mappings page defaults to active mappings only;
        flip <b>Show inactive mappings</b> to review deactivated rows.
      </>
    ),
    linkTo: "/programs",
    linkLabel: "Open Programs",
    linkTestid: "help-training-link-programs",
  },
  {
    key: "settings",
    icon: Compass,
    title: "Settings hub",
    body: (
      <>
        The <b>Settings</b> page is the entry to Bookings import,
        Availability, Coordinators, and Access. If a control isn't in
        the left rail, it lives here.
      </>
    ),
    linkTo: "/settings",
    linkLabel: "Open Settings",
    linkTestid: "help-training-link-settings",
  },
];

export function HelpTrainingDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="help-training-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="help-training-dialog-title">
            Help &amp; Training
          </DialogTitle>
          <DialogDescription>
            A short guide to the pages you use every day. Click any topic to
            jump straight to it.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4"
          data-testid="help-training-dialog-body"
        >
          {_TOPICS.map((t) => (
            <section
              key={t.key}
              data-testid={`help-training-topic-${t.key}`}
              className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4 space-y-2"
            >
              <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                <t.icon className="h-4 w-4 text-zinc-400" aria-hidden="true" />
                {t.title}
              </h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{t.body}</p>
              <Link
                to={t.linkTo}
                onClick={() => onOpenChange(false)}
                data-testid={t.linkTestid}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#D5001C] hover:underline"
              >
                {t.linkLabel} →
              </Link>
            </section>
          ))}
          <details className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4" data-testid="help-training-support-details">
            <summary className="cursor-pointer text-sm font-semibold text-zinc-100">Support details</summary>
            <div className="mt-3"><BuildStamp /></div>
          </details>
        </div>

        <div className="flex justify-end pt-2 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="help-training-dialog-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
